import React, { useState } from 'react';
import {
  X, PhoneCall, CalendarClock, CircleAlert, Lock, AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { creditosAPI, describirError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastProvider';
import { formatoMoneda, limpiarNombreGrupo } from '../utils/finanzas';
import '../styles/FormModal.css';

const TIPOS_GESTION = [
  { value: 'LLAMADA', label: 'Llamada' },
  { value: 'VISITA', label: 'Visita domiciliaria' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'MENSAJE', label: 'SMS / Mensaje de texto' },
  { value: 'OTRO', label: 'Carta de requerimiento / Otro' }
];

const RESULTADOS_GESTION = [
  { value: 'PROMESA_PAGO', label: 'Promesa de pago' },
  { value: 'PAGO_REPORTADO', label: 'Cliente reporta pago (sin registrar; lo verifica administración)' },
  { value: 'RECHAZA_PAGO', label: 'Negativa de pago' },
  { value: 'NO_CONTACTADO', label: 'No localizado / ilocalizable' },
  { value: 'REAGENDAR', label: 'En seguimiento / reagendar' },
  { value: 'CONTACTADO', label: 'Contactado' },
  { value: 'OTRO', label: 'Otro / Caso especial' }
];

const fechaHoy = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(new Date());

export default function RegistrarGestionModal({
  credito,
  saldoActual: propSaldoActual,
  saldoVencido: propSaldoVencido,
  diasAtraso: propDiasAtraso,
  hayPromesaVigente = false,
  onClose,
  onRegistrada
}) {
  const toast = useToast();
  const { hasPermission, reviewMode } = useAuth();

  const [tipoGestion, setTipoGestion] = useState('LLAMADA');
  const [resultadoGestion, setResultadoGestion] = useState('CONTACTADO');
  const [observaciones, setObservaciones] = useState('');
  const [proximaGestionEn, setProximaGestionEn] = useState('');
  
  // Subformulario Promesa
  const [fechaPromesa, setFechaPromesa] = useState('');
  const [importePrometido, setImportePrometido] = useState('');
  const [observacionesPromesa, setObservacionesPromesa] = useState('');
  
  const [enviando, setEnviando] = useState(false);

  // Extracción de datos del crédito
  const saldoActualNum = Number(propSaldoActual ?? credito?.saldoActual ?? 0);
  const saldoVencidoNum = Number(propSaldoVencido ?? credito?.saldoVencido ?? credito?.saldoVencidoReal ?? 0);
  const diasAtrasoNum = Number(propDiasAtraso ?? credito?.diasAtraso ?? 0);
  
  const clienteNombre = credito?.cliente?.nombre || credito?.titular?.nombreCompleto || credito?.clienteTitular?.nombre || '—';
  const clienteFolio = credito?.cliente?.folio || credito?.titular?.folio || credito?.clienteTitular?.folio || '';
  const creditoFolio = credito?.folio || '—';
  const modalidad = (credito?.tipoCreditoCanonico || credito?.modalidad || credito?.tipoCredito || 'INDIVIDUAL').toUpperCase();
  
  const agrupacionNombre = credito?.agrupacion
    ? limpiarNombreGrupo(credito.agrupacion.nombre).nombre
    : (credito?.nombreGrupo || credito?.grupoNombre || credito?.colocadoraNombre || null);
  const agrupacionTipo = credito?.agrupacion?.tipoAgrupacion || (modalidad === 'COLOCADORA' ? 'COLOCADORA' : (modalidad === 'GRUPAL' ? 'GRUPO' : null));
  const agrupacionFolio = credito?.agrupacion?.folio || '';

  const esPromesa = resultadoGestion === 'PROMESA_PAGO';
  const importePrometidoNum = Number(importePrometido || 0);

  // Validaciones en vivo
  let errorValidacionPromesa = null;

  if (esPromesa && !hayPromesaVigente) {
    if (!fechaPromesa) {
      errorValidacionPromesa = 'La fecha de promesa es obligatoria.';
    } else if (!importePrometido || isNaN(importePrometidoNum) || importePrometidoNum <= 0) {
      errorValidacionPromesa = 'El importe prometido debe ser mayor a $0.00.';
    } else if (importePrometidoNum > saldoActualNum) {
      errorValidacionPromesa = 'El importe prometido no puede superar el saldo actual del crédito.';
    }
  }

  const formularioValido = !esPromesa || (esPromesa && !hayPromesaVigente && !errorValidacionPromesa);

  const enviar = async (e) => {
    e.preventDefault();
    if (!formularioValido || enviando) return;

    setEnviando(true);
    try {
      const creditoId = credito?.id || credito?.creditoId;
      await creditosAPI.crearGestion(creditoId, {
        tipoGestion,
        resultadoGestion,
        observaciones: observaciones.trim() || undefined,
        proximaGestionEn: proximaGestionEn || undefined,
        promesa: esPromesa && !hayPromesaVigente && fechaPromesa
          ? {
              fechaPromesa,
              importePrometido: importePrometidoNum.toFixed(2),
              observaciones: observacionesPromesa.trim() || undefined
            }
          : undefined
      });
      if (onRegistrada) await onRegistrada();
    } catch (err) {
      toast.error(describirError(err).mensaje);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100, backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(2px)' }}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '640px', width: '100%', borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)' }}
      >
        {/* Header Modal */}
        <div className="modal-header" style={{ background: '#0f172a', color: '#ffffff', padding: '1rem 1.25rem', borderBottom: '1px solid #1e293b' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <PhoneCall size={18} style={{ color: '#38bdf8' }} />
              <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0, color: '#ffffff' }}>
                REGISTRAR GESTIÓN DE COBRANZA
              </h2>
            </div>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>
              Bitácora de seguimiento, acuerdos y compromisos de pago
            </p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Cerrar"
            style={{ color: '#94a3b8', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Resumen del Crédito */}
        <div style={{ background: '#f8fafc', padding: '0.85rem 1.25rem', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.65rem' }}>
            <div>
              <span className="text-muted text-xs" style={{ display: 'block', fontWeight: '600' }}>CLIENTE</span>
              <div className="font-semibold text-slate-800" style={{ fontSize: '0.825rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {clienteNombre}
              </div>
              {clienteFolio && <span className="font-mono text-muted text-xs">{clienteFolio}</span>}
            </div>

            <div>
              <span className="text-muted text-xs" style={{ display: 'block', fontWeight: '600' }}>FOLIO CRÉDITO</span>
              <span className="font-mono font-bold text-teal" style={{ fontSize: '0.875rem' }}>{creditoFolio}</span>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{modalidad}</div>
            </div>

            {agrupacionNombre && (
              <div>
                <span className="text-muted text-xs" style={{ display: 'block', fontWeight: '600' }}>
                  {agrupacionTipo || 'AGRUPACIÓN'}
                </span>
                <div className="font-semibold text-slate-700" style={{ fontSize: '0.825rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {agrupacionNombre}
                </div>
                {agrupacionFolio && <span className="font-mono text-muted text-xs">{agrupacionFolio}</span>}
              </div>
            )}

            <div>
              <span className="text-muted text-xs" style={{ display: 'block', fontWeight: '600' }}>SALDO VENCIDO</span>
              <span className="font-mono font-bold text-danger" style={{ fontSize: '0.9rem' }}>
                {formatoMoneda(saldoVencidoNum)}
              </span>
              <div className="text-xs font-semibold" style={{ color: diasAtrasoNum > 0 ? '#dc2626' : '#16a34a' }}>
                {diasAtrasoNum > 0 ? `${diasAtrasoNum} d atraso` : 'Al corriente'}
              </div>
            </div>

            <div>
              <span className="text-muted text-xs" style={{ display: 'block', fontWeight: '600' }}>SALDO ACTUAL</span>
              <span className="font-mono font-bold text-slate-800" style={{ fontSize: '0.9rem' }}>
                {formatoMoneda(saldoActualNum)}
              </span>
              <div className="text-xs text-muted">Cartera total</div>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <form className="modal-form" onSubmit={enviar} style={{ padding: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            <div className="form-group">
              <label htmlFor="tipoGestion" style={{ fontWeight: '600', fontSize: '0.825rem' }}>
                TIPO DE GESTIÓN *
              </label>
              <select
                id="tipoGestion"
                className="form-control form-select"
                value={tipoGestion}
                onChange={(e) => setTipoGestion(e.target.value)}
              >
                {TIPOS_GESTION.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="resultadoGestion" style={{ fontWeight: '600', fontSize: '0.825rem' }}>
                RESULTADO *
              </label>
              <select
                id="resultadoGestion"
                className="form-control form-select"
                value={resultadoGestion}
                onChange={(e) => setResultadoGestion(e.target.value)}
              >
                {RESULTADOS_GESTION.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '0.5rem' }}>
            <label htmlFor="observaciones" style={{ fontWeight: '600', fontSize: '0.825rem' }}>
              OBSERVACIONES / DETALLE DEL CONTACTO
            </label>
            <textarea
              id="observaciones"
              className="form-control"
              rows={3}
              placeholder="Acuerdos conversados, motivo del retraso, persona con quien se tuvo contacto..."
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="form-group" style={{ marginTop: '0.5rem' }}>
            <label htmlFor="proximaGestionEn" style={{ fontWeight: '600', fontSize: '0.825rem' }}>
              PRÓXIMA GESTIÓN / PRÓXIMA ACCIÓN (OPCIONAL)
            </label>
            <input
              id="proximaGestionEn"
              type="date"
              className="form-control"
              value={proximaGestionEn}
              onChange={(e) => setProximaGestionEn(e.target.value)}
            />
          </div>

          {/* Subformulario Promesa de Pago */}
          {esPromesa && (
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.65rem' }}>
                <CalendarClock size={16} className="text-emerald-700" />
                <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#166534' }}>
                  COMPROMISO DE PAGO (PROMESA)
                </h4>
              </div>

              {hayPromesaVigente ? (
                <div className="data-notice" style={{ margin: 0 }}>
                  <CircleAlert size={16} />
                  <span>
                    Ya existe una promesa vigente para este crédito. Debe cumplirse o cancelarse formalmente antes de registrar una nueva promesa.
                  </span>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label htmlFor="fechaPromesa" style={{ fontWeight: '600', fontSize: '0.8rem', color: '#166534' }}>
                        FECHA COMPROMISO *
                      </label>
                      <input
                        id="fechaPromesa"
                        type="date"
                        className="form-control"
                        min={fechaHoy()}
                        value={fechaPromesa}
                        onChange={(e) => setFechaPromesa(e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label htmlFor="importePrometido" style={{ fontWeight: '600', fontSize: '0.8rem', color: '#166534' }}>
                        IMPORTE PROMETIDO ($) *
                      </label>
                      <input
                        id="importePrometido"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={saldoActualNum}
                        className="form-control font-mono font-bold"
                        placeholder="0.00"
                        value={importePrometido}
                        onChange={(e) => setImportePrometido(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ margin: '0.5rem 0 0 0' }}>
                    <label htmlFor="observacionesPromesa" style={{ fontWeight: '600', fontSize: '0.8rem', color: '#166534' }}>
                      NOTAS DEL COMPROMISO (OPCIONAL)
                    </label>
                    <input
                      id="observacionesPromesa"
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Lugar de entrega, canal de pago prometido, etc."
                      value={observacionesPromesa}
                      onChange={(e) => setObservacionesPromesa(e.target.value)}
                      maxLength={500}
                    />
                  </div>

                  {/* Contexto financiero de la promesa */}
                  <div style={{ marginTop: '0.65rem', padding: '0.5rem 0.75rem', background: '#ffffff', borderRadius: '0.375rem', border: '1px solid #dcfce7', fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span>Saldo vencido: <strong>{formatoMoneda(saldoVencidoNum)}</strong></span>
                    <span>Saldo actual: <strong>{formatoMoneda(saldoActualNum)}</strong></span>
                    <span style={{ color: '#166534' }}>Promesa: <strong>{formatoMoneda(importePrometidoNum)}</strong></span>
                  </div>

                  {errorValidacionPromesa && (
                    <div style={{ marginTop: '0.5rem', color: '#b91c1c', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <AlertTriangle size={14} />
                      <span>{errorValidacionPromesa}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Aviso READ_ONLY */}
          {reviewMode && (
            <div style={{ marginTop: '1rem', padding: '0.65rem 0.85rem', backgroundColor: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Lock size={15} className="text-danger" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '0.78rem', color: '#991b1b', fontWeight: '500' }}>
                Modo revisión (READ_ONLY): el formulario es interactivo para validación UX, pero la escritura real está protegida.
              </span>
            </div>
          )}

          {/* Footer Modal */}
          <div className="modal-footer" style={{ marginTop: '1.25rem', paddingTop: '0.85rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cerrar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={enviando || reviewMode || !hasPermission('COBRANZA_GESTIONAR')}
              title={
                reviewMode
                  ? "Modo revisión: las escrituras están deshabilitadas."
                  : !hasPermission('COBRANZA_GESTIONAR')
                  ? "Permiso insuficiente para registrar gestiones"
                  : "Guardar gestión de cobranza"
              }
              style={{
                opacity: (enviando || reviewMode || !hasPermission('COBRANZA_GESTIONAR')) ? 0.65 : 1,
                cursor: (enviando || reviewMode || !hasPermission('COBRANZA_GESTIONAR')) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              {reviewMode ? <Lock size={14} /> : <CheckCircle2 size={14} />}
              <span>{enviando ? 'Guardando...' : 'Guardar gestión'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
